import { z } from 'zod';

/**
 * Notification keys (CLAUDE.md §3 `notification`): two-way templated messages,
 * multilingual, event-driven off the order state machine. Every key here has
 * an `en` and a `km` string in `@telestore/i18n` — the language policy in §6.2
 * allows no third display language and no hard-coded strings.
 */

export const NOTIFICATION_KEYS = [
  // customer-facing
  'order.placed',
  'order.awaiting_payment',
  'order.payment_reminder',
  'order.paid',
  'order.confirmed',
  'order.shipped',
  'order.completed',
  'order.cancelled',
  'order.refunded',
  'order.exchange_created',
  // §3 `automation` (v2): the only automated message not tied to an order.
  'customer.win_back',
  // §4 booking: 24h and 2h before an appointment, each with inline actions.
  'booking.reminder_24h',
  'booking.reminder_2h',
  'booking.rescheduled',
  // merchant-facing
  'merchant.new_order',
  'merchant.payment_received',
  'merchant.payment_unmatched',
  'merchant.low_stock',
  'merchant.cod_collected',
  'merchant.staff_joined',
  'merchant.subscription_reminder',
  'merchant.subscription_expired',
  'merchant.credits_low',
  'merchant.credits_exhausted',
  'merchant.ai_job_done',
  'merchant.provider_degraded',
  'merchant.aba_listener_broken',
  // ops-facing (founder's Telegram, §7 observability)
  'ops.unparsed_aba_notification',
  'ops.webhook_failures',
  'ops.queue_backlog',
  'ops.provider_down',
  // §6.8: the reference rate is platform-supplied, so a rate that has stopped
  // updating is our failure and nobody else's to notice.
  'ops.exchange_rate_stale',
  // §8 recruits pilot shops by hand, so a landing-page lead is a same-day ping.
  'ops.new_lead',
] as const;

export type NotificationKey = (typeof NOTIFICATION_KEYS)[number];

export const notificationKeySchema = z.enum([...NOTIFICATION_KEYS]);

export const notificationLogSchema = z.object({
  id: z.string().uuid(),
  key: notificationKeySchema,
  channel: z.enum(['bot', 'ops']),
  target: z.string(),
  status: z.enum(['queued', 'sent', 'failed']),
  error: z.string().nullable(),
  sentAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

/**
 * Merchants may override the customer-facing copy per shop. Overrides are
 * plain text with `{{placeholders}}`; we never let a merchant inject HTML into
 * a Telegram message.
 */
export const notificationOverrideSchema = z.object({
  key: notificationKeySchema,
  locale: z.enum(['km', 'en']),
  body: z.string().max(1500),
  isActive: z.boolean().default(true),
});
